import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import Navbar from '../components/layout/Navbar';
import Icon from '../components/brand/Icon';
import './InvoicesPage.css';

const MOCK_INVOICES = [
  { id: 'INV-001', customer: 'John Martinez', email: 'john@email.com', amount: 340.00, status: 'pending', due: 'Jun 25, 2026', desc: 'Landscaping service' },
  { id: 'INV-002', customer: 'Sara Lopez', email: 'sara@email.com', amount: 89.50, status: 'paid', due: 'Jun 18, 2026', desc: 'Web design consultation' },
  { id: 'INV-003', customer: 'Ana Flores', email: 'ana@email.com', amount: 200.00, status: 'overdue', due: 'Jun 10, 2026', desc: 'Photography session' },
];

const schema = Yup.object({
  customerEmail: Yup.string().email('Valid email required').required('Email is required'),
  customerName: Yup.string().required('Customer name is required'),
  amount: Yup.number().positive('Amount must be positive').required('Amount is required'),
  description: Yup.string().required('Description is required'),
  dueDate: Yup.string().required('Due date is required'),
});

const statusBadge = (status) => {
  const cfg = { paid: 'badge-success', pending: 'badge-warning', overdue: 'badge-danger' };
  return <span className={`badge ${cfg[status] || 'badge-gray'}`}>{status.charAt(0).toUpperCase() + status.slice(1)}</span>;
};

const InvoicesPage = () => {
  const [invoices, setInvoices] = useState(MOCK_INVOICES);
  const [showModal, setShowModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [filter, setFilter] = useState('all');

  const filtered = filter === 'all' ? invoices : invoices.filter(i => i.status === filter);

  const handleCreate = async (values, { resetForm }) => {
    setSending(true);
    await new Promise(r => setTimeout(r, 1000));
    const newInv = {
      id: `INV-00${invoices.length + 1}`,
      customer: values.customerName,
      email: values.customerEmail,
      amount: parseFloat(values.amount),
      status: 'pending',
      due: new Date(values.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      desc: values.description,
    };
    setInvoices(prev => [newInv, ...prev]);
    setSending(false);
    setShowModal(false);
    setSuccessMsg(`Invoice sent to ${values.customerEmail}`);
    resetForm();
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  return (
    <div className="invoices-page">
      <Navbar />
      <div className="invoices-content container">
        <div className="invoices-header">
          <div>
            <h1>Invoices</h1>
            <p>Send invoices and track payments from your customers.</p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ New Invoice</button>
        </div>

        {successMsg && <div className="alert alert-success">{successMsg} ✓</div>}

        {/* Filter Tabs */}
        <div className="invoice-filters">
          {['all', 'pending', 'paid', 'overdue'].map(f => (
            <button
              key={f}
              className={`filter-tab ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              <span className="filter-count">
                {f === 'all' ? invoices.length : invoices.filter(i => i.status === f).length}
              </span>
            </button>
          ))}
        </div>

        {/* Invoice List */}
        {filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><Icon name="doc" size={30} /></div>
            <h3>No invoices yet</h3>
            <p>Create your first invoice to get paid by your customers.</p>
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Create Invoice</button>
          </div>
        ) : (
          <div className="invoices-table card">
            <table>
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Customer</th>
                  <th>Description</th>
                  <th>Due Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv) => (
                  <tr key={inv.id}>
                    <td className="inv-id">{inv.id}</td>
                    <td>
                      <div className="inv-customer">{inv.customer}</div>
                      <div className="inv-email">{inv.email}</div>
                    </td>
                    <td className="inv-desc">{inv.desc}</td>
                    <td className="inv-due">{inv.due}</td>
                    <td className="inv-amount">${inv.amount.toFixed(2)}</td>
                    <td>{statusBadge(inv.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Invoice Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="modal-header">
              <h2>New Invoice</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <Formik
              initialValues={{ customerName: '', customerEmail: '', amount: '', description: '', dueDate: '' }}
              validationSchema={schema}
              onSubmit={handleCreate}
            >
              {() => (
                <Form className="modal-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Customer Name *</label>
                      <Field name="customerName" className="form-input" placeholder="John Martinez" />
                      <ErrorMessage name="customerName" component="div" className="form-error" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Customer Email *</label>
                      <Field name="customerEmail" type="email" className="form-input" placeholder="john@email.com" />
                      <ErrorMessage name="customerEmail" component="div" className="form-error" />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Amount ($) *</label>
                      <Field name="amount" type="number" step="0.01" min="0" className="form-input" placeholder="0.00" />
                      <ErrorMessage name="amount" component="div" className="form-error" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Due Date *</label>
                      <Field name="dueDate" type="date" className="form-input" />
                      <ErrorMessage name="dueDate" component="div" className="form-error" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description *</label>
                    <Field as="textarea" name="description" className="form-textarea" placeholder="What is this invoice for?" rows={3} />
                    <ErrorMessage name="description" component="div" className="form-error" />
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={sending}>
                      {sending ? <><span className="spinner"></span> Sending...</> : 'Send Invoice'}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesPage;
